/**
 * R-6 — derive canonical cross-field findings from SED cross-validation
 * checks and site metadata. Pure; never throws.
 */
import type { CrossValidationCheck } from '../../../types/siteEvidence';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { BopHeatRejection, ProjectIntent } from '../../../types/siteRag';
import type { CrossFieldFinding, CrossFieldSeverity } from './types';

/**
 * P0-4 — heat-rejection SINKS that withdraw water at scale. Water *withdrawal*
 * (and thus the High-water-stress conflict) is driven by the cold-END sink
 * (`bopHeatRejection`), NOT the rack-side `coolingStrategy` (air vs liquid).
 * A liquid-rack + dry-cooled site withdraws ~no water; an air-rack + wet-tower
 * site withdraws a lot. Hybrid partially evaporates, so it counts as a wet sink.
 *
 * EXPORTED as the SHARED, single source of truth for "which heat-rejection
 * sink drives water withdrawal" so the Path-to-GO water gate
 * (`mandatoryReports.ts`) and the SED-gap water relevance
 * (`providerImpactForIntent.ts`) gate on the SAME axis the cross-field guard
 * does (no drift between the guard, the gate, and the recommendation).
 */
export const WATER_WITHDRAWING_SINKS: ReadonlySet<BopHeatRejection> = new Set([
  'wet-cooling-tower',
  'hybrid',
  'once-through',
]);

const RULE_ALIASES: Record<string, CrossFieldFinding['ruleId']> = {
  'grid-distance-capacity': 'capacityVsInterconnection',
  'research-water-rights-stress': 'coolingVsWater',
  'water-humidity-note': 'coolingVsWater',
  'research-moratorium-vs-stance': 'nuclearAdjacencyVsIntent',
  'nuclear-stance-infra': 'nuclearAdjacencyVsIntent',
  'elevation-flood-consistency': 'acreageVsMW',
};

function mapSeverity(severity: CrossValidationCheck['severity'], passed: boolean): CrossFieldSeverity | null {
  if (passed) {
    return severity === 'info' ? 'info' : null;
  }
  if (severity === 'error') return 'block';
  if (severity === 'warning') return 'warn';
  if (severity === 'info') return 'info';
  return null;
}

function fromCrossValidationCheck(check: CrossValidationCheck): CrossFieldFinding | null {
  const ruleId = RULE_ALIASES[check.rule];
  if (!ruleId) return null;
  const severity = mapSeverity(check.severity, check.passed);
  if (!severity) return null;
  return { ruleId, severity, message: check.message };
}

function fromSedHeuristics(
  sed: SiteEvidenceDocument,
  projectIntent?: ProjectIntent | null,
): CrossFieldFinding[] {
  const out: CrossFieldFinding[] = [];

  const grid = sed.grid.status === 'verified' ? sed.grid.data : null;
  if (grid) {
    if (grid.substationDistKm < 2 && grid.capacity === 'Constrained') {
      out.push({
        ruleId: 'capacityVsInterconnection',
        severity: 'warn',
        message: `Nearby substation (${grid.substationDistKm}km) with constrained capacity`,
      });
    }
    if (grid.substationDistKm > 20 && grid.capacity === 'High') {
      out.push({
        ruleId: 'capacityVsInterconnection',
        severity: 'info',
        message: `Substation ${grid.substationDistKm}km away but capacity marked High`,
      });
    }
  }

  // P0-4 — the water-conflict BLOCK must gate on the heat-rejection SINK
  // (`bopHeatRejection`), which drives water *withdrawal*, NOT on the rack-side
  // `coolingStrategy` (air vs liquid), which is orthogonal to water use. The old
  // code falsely blocked a liquid-rack + dry-cooled site (near-zero water) and
  // failed to flag an air-rack + wet-tower site (high water).
  const waterStress = sed.waterStress.status === 'verified' ? sed.waterStress.data?.stressLevel : null;
  const sink = projectIntent?.bopHeatRejection;
  const rackCooling = projectIntent?.coolingStrategy;
  if (waterStress === 'High') {
    if (sink != null && WATER_WITHDRAWING_SINKS.has(sink)) {
      out.push({
        ruleId: 'coolingVsWater',
        severity: 'block',
        message: `High water stress with a water-withdrawing heat-rejection sink (${sink})`,
      });
    } else if (rackCooling === 'liquid' || rackCooling === 'hybrid') {
      // Liquid/hybrid rack cooling is NOT itself a water-withdrawal driver, but
      // when the heat-rejection sink is unspecified a wet sink could still be
      // chosen — surface a lower-severity note rather than a hard block.
      out.push({
        ruleId: 'coolingVsWater',
        severity: 'warn',
        message: `High water stress; ${rackCooling} rack cooling — confirm the heat-rejection sink is dry/closed-loop to limit water withdrawal`,
      });
    } else {
      out.push({
        ruleId: 'coolingVsWater',
        severity: 'warn',
        message: 'High WRI water stress — verify cooling water availability',
      });
    }
  }

  const acres = sed.siteProfile.status === 'verified' ? sed.siteProfile.data?.acres : null;
  const capacityMw = projectIntent?.powerRequirementMW ?? null;
  if (typeof acres === 'number' && acres > 0 && typeof capacityMw === 'number' && capacityMw > 0) {
    const requiredAcres = capacityMw * 6;
    if (acres < requiredAcres * 0.5) {
      out.push({
        ruleId: 'acreageVsMW',
        severity: 'block',
        message: `${acres} acres insufficient for ~${capacityMw} MW (~${requiredAcres.toFixed(0)} acres typical)`,
      });
    } else if (acres < requiredAcres) {
      out.push({
        ruleId: 'acreageVsMW',
        severity: 'warn',
        message: `${acres} acres tight for ${capacityMw} MW solar footprint`,
      });
    }
  } else if (typeof acres === 'number' && acres < 50) {
    out.push({
      ruleId: 'acreageVsMW',
      severity: 'warn',
      message: `Only ${acres} acres available — verify land sufficiency for target capacity`,
    });
  }

  const nuclearInterest = projectIntent?.nuclearInterest;
  const reactors = sed.nuclearInfra.status === 'verified'
    ? sed.nuclearInfra.data?.reactorsWithin200km ?? 0
    : null;
  const stance = sed.regulatory.status === 'verified' ? sed.regulatory.data?.nuclearStance : null;
  if (nuclearInterest === 'no' && typeof reactors === 'number' && reactors > 0) {
    out.push({
      ruleId: 'nuclearAdjacencyVsIntent',
      severity: 'warn',
      message: `Nuclear interest "no" but ${reactors} reactor(s) within 200km`,
    });
  }
  if (stance === 'Restrictive' && (nuclearInterest === 'primary' || nuclearInterest === 'considering')) {
    out.push({
      ruleId: 'nuclearAdjacencyVsIntent',
      severity: 'block',
      message: `Restrictive nuclear stance conflicts with intent "${nuclearInterest}"`,
    });
  }
  if (stance === 'Favorable' && typeof reactors === 'number' && reactors === 0) {
    out.push({
      ruleId: 'nuclearAdjacencyVsIntent',
      severity: 'info',
      message: 'Favorable nuclear stance but no commercial reactors within 200km',
    });
  }

  return out;
}

/** Collect cross-field findings for review-loop scoring. Never throws. */
export function extractCrossFieldFindings(
  sed: SiteEvidenceDocument,
  projectIntent?: ProjectIntent | null,
): CrossFieldFinding[] {
  try {
    const fromChecks = (sed.crossValidation?.checks ?? [])
      .map(fromCrossValidationCheck)
      .filter((f): f is CrossFieldFinding => f != null);

    const fromHeuristics = fromSedHeuristics(sed, projectIntent);
    return [...fromChecks, ...fromHeuristics];
  } catch (e) {
    console.warn('[R-6:crossField] extractCrossFieldFindings failed (non-blocking):', e);
    return [];
  }
}
